import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Card, CardContent, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { createOrganizationApi } from "@/graphql/api";
import { generatePassword, copyPasswordToClipboard } from "@/utils/passwordUtils";
import toast from "react-hot-toast";

const schema = z.object({
  organizationCode: z.string()
    .min(1, "組織コードを入力してください")
    .max(50, "組織コードは50文字以内で入力してください")
    .regex(
      /^[a-z0-9_-]+$/,
      "組織コードには小文字のアルファベット、数字、アンダースコア(_)、ダッシュ(-)のみ使用できます"
    ),
  organizationName: z.string().min(1, "組織名を入力してください"),
  userName: z.string().min(1, "管理者名を入力してください"),
  email: z.string().email("有効なメールアドレスを入力してください"),
  password: z.string()
    .min(8, "パスワードは8文字以上で入力してください")
    .regex(/[A-Z]/, "パスワードには大文字を含める必要があります")
    .regex(/[a-z]/, "パスワードには小文字を含める必要があります")
    .regex(/[0-9]/, "パスワードには数字を含める必要があります"),
  apiKey: z.string().min(1, "APIキーを入力してください"),
});

type FormData = z.infer<typeof schema>;


export const CreateOrganizationPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });
  
  // パスワード自動生成ハンドラー
  const handleGeneratePassword = () => {
    const password = generatePassword();
    setValue('password', password, { shouldValidate: true });
    
    // クリップボードにコピー
    copyPasswordToClipboard(password)
      .then(success => {
        if (success) {
          toast.success('パスワードをクリップボードにコピーしました');
        }
      });
  };

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError(undefined);
    
    try {
      await createOrganizationApi(
        data.apiKey,
        data.organizationCode,
        data.organizationName,
        data.userName,
        data.email,
        data.password
      )
      // 作成成功後、完了画面に遷移
      navigate("/paradis/organization/complete", {
        state: {
          email: data.email,
          password: data.password,
        },
      });

    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : "組織の作成に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
        }}
      >
        <Card sx={{ width: "100%", maxWidth: 600, mx: 2 }}>
          <CardContent>
            <Typography variant="h5" component="h1" gutterBottom textAlign="center">
              組織登録
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={4}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    アクセス情報
                  </Typography>
                  <TextField
                    {...register("apiKey")}
                    error={!!errors.apiKey}
                    helperText={errors.apiKey?.message}
                    fullWidth
                    label="APIキー"
                    type="password"
                  />
                </Box>

                <Box>
                  <Typography variant="h6" gutterBottom>
                    組織情報
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    登録する組織の情報を入力してください
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      {...register("organizationCode")}
                      error={!!errors.organizationCode}
                      helperText={errors.organizationCode?.message}
                      fullWidth
                      label="組織コード"
                    />
                    <TextField
                      {...register("organizationName")}
                      error={!!errors.organizationName}
                      helperText={errors.organizationName?.message}
                      fullWidth
                      label="組織名"
                      autoComplete="organization"
                    />
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="h6" gutterBottom>
                    管理者情報
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    組織の管理者として登録されるユーザーの情報を入力してください
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      {...register("userName")}
                      error={!!errors.userName}
                      helperText={errors.userName?.message}
                      fullWidth
                      label="管理者名"
                      autoComplete="name"
                    />

                    <TextField
                      {...register("email")}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      fullWidth
                      label="メールアドレス"
                      type="email"
                      autoComplete="email"
                    />

                    <TextField
                      {...register("password")}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      fullWidth
                      label="パスワード"
                      type="password"
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              onClick={handleGeneratePassword}
                              size="small"
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              自動生成
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </Box>

                <Box sx={{ pt: 2 }}>
                  <LoadingButton
                    loading={isLoading}
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                  >
                    登録
                  </LoadingButton>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};
